"use client";

import type { Route } from "@/infrastructure/router/types";
import Link from "@/infrastructure/router/Link";
import { usePathname, useRouter } from "@/infrastructure/router/navigation";
import { staticAssetPath } from "@/infrastructure/router/StaticImage";
import { type ReactNode, useEffect, useState } from "react";
import {
	ArtistSessionProvider,
	useArtistSession,
} from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	type ArtistIconName,
	useDSAppearance,
} from "@/components/design-system/ArtistDesignSystem";
import { useArtistRuntimeConfiguration } from "@/components/runtime/ArtistRuntimeConfigurationProvider";
import { useTranslation, type TranslationKey } from "@/i18n/ArtistI18n";
import { CreateDialog } from "./CreateDialog";
import {
	beginnerTutorialStore,
	useBeginnerTutorial,
} from "@/domain/tutorial/BeginnerTutorial";

interface TabDescriptor {
	readonly href: Route;
	readonly labelKey: TranslationKey;
	readonly shortLabelKey: TranslationKey;
	readonly icon: ArtistIconName;
}

const TABS: readonly TabDescriptor[] = [
	{
		href: "/discover",
		labelKey: "tabs.home",
		shortLabelKey: "tabs.home",
		icon: "home",
	},
	{
		href: "/player",
		labelKey: "tabs.play",
		shortLabelKey: "tabs.play",
		icon: "music",
	},
	{
		href: "/genart",
		labelKey: "tabs.generate",
		shortLabelKey: "tabs.generate",
		icon: "generate",
	},
	{
		href: "/library",
		labelKey: "tabs.me",
		shortLabelKey: "tabs.me",
		icon: "person",
	},
];

function isLibraryActive(pathname: string): boolean {
	return (
		pathname.startsWith("/library") ||
		pathname.startsWith("/account") ||
		pathname.startsWith("/settings") ||
		pathname.startsWith("/messages")
	);
}

export function ArtistShell({
	children,
}: Readonly<{
	children: ReactNode;
}>) {
	const runtimeConfiguration = useArtistRuntimeConfiguration();
	return (
		<ArtistSessionProvider
			providerConfiguration={runtimeConfiguration.providerConfiguration}
		>
			<ArtistShellContent>{children}</ArtistShellContent>
		</ArtistSessionProvider>
	);
}

function ArtistShellContent({ children }: Readonly<{ children: ReactNode }>) {
	const pathname = usePathname();
	const router = useRouter();
	const [createOpen, setCreateOpen] = useState(false);
	const [promotionOpen, setPromotionOpen] = useState(false);
	const auth = useArtistSession();
	const tutorial = useBeginnerTutorial();
	const { t } = useTranslation();
	useDSAppearance();

	useEffect(() => {
		if (tutorial.step !== "inactive") {
			setPromotionOpen(false);
			return;
		}
		if (pathname === "/promotion") return;
		const key = "artist.promotion.launch-ad.v1";
		if (window.sessionStorage.getItem(key) === "dismissed") return;
		setPromotionOpen(true);
	}, [pathname, tutorial.step]);

	useEffect(() => {
		if (tutorial.step !== "openLibrary") return;
		beginnerTutorialStore.advance("openLibrary", "openTutorialTrack");
		router.push("/library");
	}, [router, tutorial.step]);

	useEffect(() => {
		if (tutorial.step === "inactive" || tutorial.step === "openLibrary") return;
		const expectedRoute = ["intro", "selectMusicPrompt", "generateMusic"].includes(tutorial.step)
			? "/genart"
			: tutorial.step === "openTutorialTrack"
				? "/library"
				: "/tutorial/circle-dance";
		if (pathname !== expectedRoute) router.replace(expectedRoute);
	}, [pathname, router, tutorial.step]);

	const tutorialNavigationAllowed = (href: Route): boolean => {
		if (tutorial.step === "inactive") return true;
		if (["intro", "selectMusicPrompt", "generateMusic"].includes(tutorial.step)) {
			return href === "/genart";
		}
		if (tutorial.step === "openLibrary" && href === "/library") {
			beginnerTutorialStore.advance("openLibrary", "openTutorialTrack");
			return true;
		}
		return false;
	};

	const dismissPromotion = () => {
		window.sessionStorage.setItem("artist.promotion.launch-ad.v1", "dismissed");
		setPromotionOpen(false);
	};

	return (
		<div className="app-shell">
			<a className="skip-link" href="#main-content">
				{t("common.skipToContent")}
			</a>
			<aside className="desktop-rail" aria-label={t("common.skipToContent")}>
				<Link className="wordmark" href="/discover" aria-label="Artist" onClick={(event) => {
					if (tutorial.step !== "inactive") event.preventDefault();
				}}>
					<span className="brand-logo brand-logo--rail" aria-hidden="true" />
				</Link>
				<nav className="rail-links">
					{TABS.slice(0, 2).map((item) => (
						<NavigationLink
							key={item.href}
							item={item}
							active={pathname.startsWith(item.href)}
							label={t(item.labelKey)}
							onNavigate={() => tutorialNavigationAllowed(item.href)}
						/>
					))}
					<button
						className="create-button"
						type="button"
						onClick={() => setCreateOpen(true)}
						disabled={tutorial.step !== "inactive"}
						aria-haspopup="dialog"
						aria-label={t("tabs.create")}
					>
						<ArtistIcon name="create" size={22} />
						<span>{t("tabs.create")}</span>
					</button>
					{TABS.slice(2).map((item) => (
						<NavigationLink
							key={item.href}
							item={item}
							active={
								item.href === "/library"
									? isLibraryActive(pathname)
									: pathname.startsWith(item.href)
							}
							label={t(item.labelKey)}
							onNavigate={() => tutorialNavigationAllowed(item.href)}
						/>
					))}
				</nav>
				<Link
					className="rail-account"
					href={auth.phase === "user" ? "/account" : "/auth"}
					onClick={(event) => {
						if (tutorial.step !== "inactive") event.preventDefault();
					}}
				>
					<span className="rail-account__avatar" aria-hidden="true">
						<ArtistIcon name="person" size={20} />
					</span>
				</Link>
				<Link className="rail-settings" href="/settings" onClick={(event) => {
					if (tutorial.step !== "inactive") event.preventDefault();
				}}>
					{t("settings.title")}
				</Link>
			</aside>

			<header className="mobile-header">
				<Link className="wordmark" href="/discover" aria-label="Artist" onClick={(event) => {
					if (tutorial.step !== "inactive") event.preventDefault();
				}}>
					<span className="brand-logo brand-logo--mobile" aria-hidden="true" />
				</Link>
				<Link
					className="mobile-account"
					href={auth.phase === "user" ? "/account" : "/auth"}
					onClick={(event) => {
						if (tutorial.step !== "inactive") event.preventDefault();
					}}
					aria-label={
						auth.phase === "user"
							? t("settings.account.editProfile")
							: t("settings.account.login")
					}
				>
					<ArtistIcon name="person" size={18} />
				</Link>
			</header>
			<main
				id="main-content"
				className="main-content"
				tabIndex={-1}
			>
				{children}
			</main>

			<nav className="mobile-nav" aria-label={t("common.skipToContent")}>
					{TABS.slice(0, 2).map((item) => (
						<NavigationLink
							key={item.href}
							item={item}
							active={pathname.startsWith(item.href)}
							label={t(item.shortLabelKey)}
							onNavigate={() => tutorialNavigationAllowed(item.href)}
							mobile
						/>
					))}
					<button
						className="mobile-create"
						type="button"
						onClick={() => setCreateOpen(true)}
						disabled={tutorial.step !== "inactive"}
						aria-label={t("tabs.create")}
						aria-haspopup="dialog"
					>
						<ArtistIcon name="create" size={24} />
						<span>{t("tabs.create")}</span>
					</button>
					{TABS.slice(2).map((item) => (
						<NavigationLink
							key={item.href}
							item={item}
							active={
								item.href === "/library"
									? isLibraryActive(pathname)
									: pathname.startsWith(item.href)
							}
							label={t(item.shortLabelKey)}
							onNavigate={() => tutorialNavigationAllowed(item.href)}
							mobile
						/>
					))}
			</nav>
			<CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
			{promotionOpen ? (
				<div className="launch-promotion" role="dialog" aria-modal="true" aria-label={t("promotion.openAd")}>
					<div className="launch-promotion__content">
						{/* biome-ignore lint/performance/noImgElement: synchronized product campaign asset must retain the exact source bitmap and aspect ratio. */}
						<img
							src={staticAssetPath("/images/free-suno-promotion.png")}
							alt={t("promotion.openAd")}
							className="launch-promotion__image"
						/>
						<button
							type="button"
							className="ds-button ds-button--primary ds-button--md ds-button--expands"
							onClick={() => {
								dismissPromotion();
								router.push("/promotion");
							}}
						>
							{t("promotion.openAd")}
						</button>
						<button
							type="button"
							className="ds-button ds-button--secondary ds-button--sm"
							onClick={dismissPromotion}
						>
							{t("common.close")}
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
}

type NavigationItem = (typeof TABS)[number];

function NavigationLink({
	item,
	active,
	label,
	onNavigate,
}: Readonly<{
	item: NavigationItem;
	active: boolean;
	label: string;
	mobile?: boolean;
	onNavigate?: () => boolean;
}>) {
	return (
		<Link
			href={item.href}
			className={active ? "nav-link nav-link--active" : "nav-link"}
			aria-current={active ? "page" : undefined}
			onClick={(event) => {
				if (onNavigate?.() === false) event.preventDefault();
			}}
		>
			<span className="nav-mark" aria-hidden="true">
				<ArtistIcon name={item.icon} size={20} />
			</span>
			<span>{label}</span>
		</Link>
	);
}
